import { useEffect, useState } from "react";
import { api, ROUTES } from "./api";
import { useAdmin, useAsync, Field, ImageInput, ErrorState, Loading, PageHead } from "./ui";

const ICONS = ["leaf", "shield", "people", "herb", "shirt", "jewel", "lock"];

function CardsEditor({ label, items, onChange }) {
  const update = (i, k, v) => onChange(items.map((it, x) => (x === i ? { ...it, [k]: v } : it)));
  return (
    <div className="span-2">
      <span className="field-label">{label}</span>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 8, margin: "8px 0" }}>
          <input
            className="input"
            style={{ flex: 1 }}
            value={it.title}
            placeholder="Title"
            onChange={(e) => update(i, "title", e.target.value)}
          />
          <select className="input" style={{ width: 140 }} value={it.icon} onChange={(e) => update(i, "icon", e.target.value)}>
            {ICONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button type="button" className="btn sm danger-ghost" onClick={() => onChange(items.filter((_, x) => x !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn sm" onClick={() => onChange([...items, { title: "", icon: "leaf" }])}>
        Add card
      </button>
    </div>
  );
}

export default function About() {
  const { data, loading, error, reload } = useAsync(() => api.get(ROUTES.about));
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (loading || !data) return <Loading />;
  return <AboutForm initial={data} onSaved={reload} />;
}

function AboutForm({ initial, onSaved }) {
  const { toast } = useAdmin();
  const { hero_img, bottom_img, ...text } = initial;
  const [f, setF] = useState(text);
  const [heroFile, setHeroFile] = useState(null);
  const [bottomFile, setBottomFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const form = new FormData();
      form.append("data", JSON.stringify(f));
      if (heroFile) form.append("hero_img", heroFile);
      if (bottomFile) form.append("bottom_img", bottomFile);
      await api.upload("PUT", ROUTES.about, form);
      toast("About page saved");
      setHeroFile(null);
      setBottomFile(null);
      onSaved();
    } catch (err) {
      toast(err.message, "err");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHead
        title="About page"
        sub="Edit everything shown on the About Us page"
        actions={<button className="btn primary" form="about-form" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>}
      />
      <form id="about-form" onSubmit={submit} className="form-grid">
        <Field label="Hero title"><input className="input" value={f.heroTitle} onChange={set("heroTitle")} /></Field>
        <Field label="Hero subtitle"><input className="input" value={f.heroSubtitle} onChange={set("heroSubtitle")} /></Field>
        <div className="span-2">
          <span className="field-label">Hero banner image</span>
          <ImageInput current={hero_img} file={heroFile} onChange={setHeroFile} />
        </div>

        <Field label="Mission title"><input className="input" value={f.missionTitle} onChange={set("missionTitle")} /></Field>
        <div className="span-2">
          <Field label="Mission text"><textarea className="input" rows="4" value={f.missionText} onChange={set("missionText")} /></Field>
        </div>
        <CardsEditor label="Mission cards" items={f.mission} onChange={(v) => setF((s) => ({ ...s, mission: v }))} />

        <Field label="Why choose us title"><input className="input" value={f.whyTitle} onChange={set("whyTitle")} /></Field>
        <CardsEditor label="Why choose us cards" items={f.why} onChange={(v) => setF((s) => ({ ...s, why: v }))} />

        <Field label="Bottom heading"><input className="input" value={f.bottomTitle} onChange={set("bottomTitle")} /></Field>
        <Field label="Bottom text"><input className="input" value={f.bottomText} onChange={set("bottomText")} /></Field>
        <Field label="Button text"><input className="input" value={f.buttonText} onChange={set("buttonText")} /></Field>
        <Field label="Button link" hint="For example /shop"><input className="input" value={f.buttonLink} onChange={set("buttonLink")} /></Field>
        <div className="span-2">
          <span className="field-label">Bottom banner image</span>
          <ImageInput current={bottom_img} file={bottomFile} onChange={setBottomFile} />
        </div>
      </form>
    </>
  );
}